import { Helmet } from "react-helmet-async";

/**
 * SEO Component for consistent meta tags across pages
 * @param {string} title - Page title
 * @param {string} description - Meta description (max 160 chars)
 * @param {string} keywords - Comma-separated keywords
 * @param {string} canonicalUrl - Canonical URL for the page
 * @param {string} ogImage - Open Graph image URL
 * @param {string} ogType - Open Graph type (website, article, product)
 * @param {object} structuredData - JSON-LD structured data
 */
export default function SEO({
  title = "Eduzy - AI-Powered Learning for Grades 5-8",
  description = "Personalized AI tutoring, homework help, and question banks for students in grades 5-8. Learn in 11 Indian languages with adaptive AI technology.",
  keywords = "AI tutor, homework help, online learning, CBSE, grades 5-8, personalized learning, India education",
  canonicalUrl,
  ogImage = "/og-image.png",
  ogType = "website",
  structuredData,
  noIndex = false,
}) {
  const siteUrl = "https://eduzy.co";
  const fullCanonicalUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content="Eduzy" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      {/* Additional Meta */}
      <meta name="author" content="Eduzy by Contentium Tech" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="language" content="English" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-defined structured data templates
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Eduzy",
  "url": "https://eduzy.co",
  "logo": "https://eduzy.co/eduzy-logo-transparent.png",
  "description": "AI-powered personalized learning platform for grades 5-8 in India",
  "sameAs": [],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "support@eduzy.co"
  }
};

export const educationalOrganizationSchema = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "name": "Eduzy",
  "url": "https://eduzy.co",
  "description": "AI-powered personalized learning platform for students in grades 5-8",
  "educationalCredentialAwarded": "Certificate of Completion",
  "areaServed": {
    "@type": "Country",
    "name": "India"
  }
};

export const productSchema = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": product.name,
  "description": product.description,
  "brand": {
    "@type": "Brand",
    "name": "Eduzy"
  },
  "offers": {
    "@type": "Offer",
    "price": product.price,
    "priceCurrency": "INR",
    "availability": "https://schema.org/InStock"
  }
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://eduzy.co${item.url}`
  }))
});

export const serviceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": service.name,
  "description": service.description,
  "provider": {
    "@type": "Organization",
    "name": "Eduzy"
  },
  "areaServed": {
    "@type": "Country",
    "name": "India"
  },
  "offers": service.price ? {
    "@type": "Offer",
    "price": service.price,
    "priceCurrency": "INR"
  } : undefined
});
